<template>
  <div class="create-chatbot">
    <Notification
      v-model:show="showNotification"
      :message="notificationMessage"
      :type="notificationType"
    />

    <div class="cc-wrap">
      <button class="cc-back" @click="$router.back()">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        <span>{{ t('createAgent.back') }}</span>
      </button>

      <div v-if="loading" class="cc-loading">
        <div class="cc-spinner"></div>
        <p>{{ t('createAgent.loading') }}</p>
      </div>

      <div v-else class="cc-card">
        <!-- Bandeau d'en-tête -->
        <div class="cc-hero">
          <span class="cc-eyebrow">{{ t('createAgent.eyebrow') }}</span>
          <h1>{{ t('createAgent.title') }}</h1>
          <p>{{ t('createAgent.subtitle') }}</p>
        </div>

        <!-- Avatar (aperçu live) chevauchant le bandeau -->
        <div class="cc-avatar-zone">
          <label for="avatar-upload" class="cc-avatar" title="Ajouter une photo">
            <img :src="previewAvatar" alt="Avatar de l'agent">
            <span class="cc-avatar-edit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </span>
          </label>
          <input id="avatar-upload" type="file" accept="image/jpeg,image/jpg,image/png" @change="handleAvatarUpload" hidden>
          <span class="cc-avatar-helper">{{ avatarPreview ? t('createAgent.photoAdded') : t('createAgent.photoHint') }}</span>
        </div>

        <div class="cc-body">
          <!-- Nom -->
          <div class="cc-name-field">
            <label>{{ t('createAgent.nameLabel') }}</label>
            <input type="text" v-model="chatbot.bot_name" :placeholder="t('createAgent.namePlaceholder')" @keyup.enter="createChatbot">
            <span class="cc-hint">{{ t('createAgent.nameHint') }}</span>
          </div>

        <!-- Toggle options avancées -->
        <button type="button" class="cc-more" :class="{ open: showAdvanced }" @click="showAdvanced = !showAdvanced">
          <span>{{ showAdvanced ? t('createAgent.hideOptions') : t('createAgent.moreOptions') }}</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </button>

        <transition name="cc-collapse">
          <div v-if="showAdvanced" class="cc-advanced">
            <div class="cc-field-row">
              <div class="cc-field">
                <label>{{ t('createAgent.statusLabel') }}</label>
                <div class="cc-select">
                  <select v-model="chatbot.status">
                    <option value="active">{{ t('createAgent.statusActive') }}</option>
                    <option value="inactive">{{ t('createAgent.statusInactive') }}</option>
                    <option value="maintenance">{{ t('createAgent.statusMaintenance') }}</option>
                  </select>
                  <svg class="cc-select-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>
              <div class="cc-field">
                <label>{{ t('createAgent.emailLabel') }}</label>
                <input type="email" v-model="chatbot.email" placeholder="contact@example.com">
              </div>
            </div>

            <div class="cc-field">
              <label>{{ t('createAgent.descriptionLabel') }}</label>
              <textarea v-model="chatbot.description" :placeholder="t('createAgent.descriptionPlaceholder')"></textarea>
            </div>

            <div class="cc-field">
              <label>{{ t('createAgent.channelLabel') }}</label>
              <div class="cc-channels">
                <!-- Web : actif, canal par défaut -->
                <button type="button" class="cc-channel" :class="{ active: chatbot.type === 'web' }" @click="chatbot.type = 'web'">
                  <span class="cc-channel-icon web">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  </span>
                  <span class="cc-channel-name">Web</span>
                  <span class="cc-channel-desc">{{ t('createAgent.channelWebDesc') }}</span>
                  <svg v-if="chatbot.type === 'web'" class="cc-channel-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                </button>

                <!-- WhatsApp : actif -->
                <button type="button" class="cc-channel" :class="{ active: chatbot.type === 'whatsapp' }" @click="chatbot.type = 'whatsapp'">
                  <span class="cc-channel-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M3 21l1.65 -3.8a9 9 0 1 1 3.4 2.9l-5.05 .9"/>
                      <path d="M9 10a.5 .5 0 0 0 1 0v-1a.5 .5 0 0 0 -1 0v1a5 5 0 0 0 5 5h1a.5 .5 0 0 0 0 -1h-1a.5 .5 0 0 0 0 1"/>
                    </svg>
                  </span>
                  <span class="cc-channel-name">WhatsApp</span>
                  <span class="cc-channel-desc">{{ t('createAgent.channelWhatsappDesc') }}</span>
                  <svg v-if="chatbot.type === 'whatsapp'" class="cc-channel-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                </button>

                <!-- Telegram : bientôt -->
                <div class="cc-channel disabled">
                  <span class="cc-channel-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </span>
                  <span class="cc-channel-name">Telegram</span>
                  <span class="cc-channel-desc">{{ t('createAgent.channelSoon') }}</span>
                </div>
              </div>
            </div>

            <!-- Configuration WhatsApp (repliable) -->
            <div v-if="chatbot.type === 'whatsapp'" class="cc-subsection">
              <button type="button" class="cc-sub-toggle" :class="{ open: showWhatsAppConfig }" @click="toggleWhatsAppConfig">
                <span>{{ t('createAgent.whatsappConfig') }}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div v-if="showWhatsAppConfig" class="cc-sub-body">
                <div class="cc-field">
                  <label>{{ t('createAgent.waNumberLabel') }}</label>
                  <PhoneInput v-model="chatbot.display_phone_number" @country-change="handlePhoneChange" :placeholder="t('createAgent.waNumberPlaceholder')" />
                </div>
                <div class="cc-field">
                  <label>{{ t('createAgent.waPhoneIdLabel') }}</label>
                  <input type="text" v-model="chatbot.phone_number_id" placeholder="phone_number_id">
                </div>
                <div class="cc-field">
                  <label>{{ t('createAgent.waAppIdLabel') }}</label>
                  <input type="text" v-model="chatbot.app_id" placeholder="app_id">
                </div>
                <div class="cc-field">
                  <label>{{ t('createAgent.waTokenLabel') }}</label>
                  <div class="cc-pass">
                    <input :type="showPassword ? 'text' : 'password'" v-model="chatbot.access_token.env.WHATSAPP_TOKEN" placeholder="Token WhatsApp">
                    <button type="button" class="cc-eye" @click="togglePasswordVisibility" title="Afficher/Masquer">
                      <svg v-if="showPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>

          <div class="cc-actions">
            <button class="cc-cancel" @click="$router.back()">{{ t('createAgent.cancel') }}</button>
            <button class="cc-create" @click="createChatbot" :disabled="loading || !chatbot.bot_name">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5v14"/></svg>
              {{ t('createAgent.create') }}
            </button>
          </div>
        </div><!-- /cc-body -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PhoneInput from './PhoneInput.vue';
import Notification from './Notification.vue';

const { t } = useI18n();

// Définition directe de l'URL de l'API
const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'https://api.peelo.io';
let axios;
import('axios').then(module => {
  axios = module.default;
}).catch(err => {
  console.error('Erreur importation axios:', err);
});

const route = useRoute();

// Avatar par défaut : un robot généré (cohérent avec l'affichage des agents ailleurs).
const defaultAvatar = 'https://api.dicebear.com/9.x/bottts-neutral/svg?seed=peelo-agent';

const chatbot = ref({
  bot_name: '',
  type: 'web', // Web actif par défaut (widget site) — pas de config requise
  status: 'active', // Default to active
  description: '',
  email: '',
  avatar: '',
  display_phone_number: '',
  phone_number_id: '',
  app_id: '',
  access_token: {
    env: {
      WHATSAPP_TOKEN: ''
    }
  }
});

const loading = ref(false);
const error = ref(null);
const showPassword = ref(false);
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationType = ref('success');
const avatarPreview = ref(null);
const phoneCountry = ref(null);
const showAdvanced = ref(false); // section "Plus d'options" repliée par défaut
const showWhatsAppConfig = ref(false); // Default CLOSED

// Aperçu live de l'avatar : photo uploadée, sinon un robot généré à partir du nom
// (change pendant qu'on tape), sinon un robot par défaut.
const previewAvatar = computed(() => {
  if (avatarPreview.value) return avatarPreview.value;
  const name = (chatbot.value.bot_name || '').trim();
  if (name) return `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${encodeURIComponent(name)}`;
  return defaultAvatar;
});

const showNotificationMessage = (message, type = 'success') => {
  notificationMessage.value = message;
  notificationType.value = type;
  showNotification.value = true;
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const toggleWhatsAppConfig = () => {
  showWhatsAppConfig.value = !showWhatsAppConfig.value;
};

const handleAvatarUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    showNotificationMessage(t('createAgent.msgBadFormat'));
    return;
  }

  avatarPreview.value = URL.createObjectURL(file);
  chatbot.value.avatarFile = file;
};

const handlePhoneChange = (country) => {
  phoneCountry.value = country;
};

const createChatbot = async () => {
  try {
    if (!chatbot.value.bot_name) {
      showNotificationMessage(t('createAgent.msgNameRequired'), 'error');
      return;
    }
    loading.value = true;
    const userData = localStorage.getItem('user');
    if (!userData) {
      showNotificationMessage(t('createAgent.msgNoUser'), 'error');
      return;
    }

    let token;
    try {
      token = JSON.parse(userData).data.token;
    } catch (parseError) {
      showNotificationMessage(t('createAgent.msgBadUser'), 'error');
      return;
    }

    const chatbotData = {
      bot_name: chatbot.value.bot_name,
      type: chatbot.value.type,
      status: chatbot.value.status,
      description: chatbot.value.description,
      email: chatbot.value.email,
      display_phone_number: chatbot.value.display_phone_number,
      phone_number_id: chatbot.value.phone_number_id,
      app_id: chatbot.value.app_id,
      access_token: chatbot.value.access_token
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(chatbotData));
    if (chatbot.value.avatarFile) {
      formData.append('avatar', chatbot.value.avatarFile);
    }

    const response = await axios.post(
      `${apiBaseUrl}/create/strong/chatbot`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    showNotificationMessage(t('createAgent.msgSuccess'), 'success');

    const returnTo = route.query.returnTo;
    const openPopupWhatsapp = route.query.openpopupwhatsapp;
    const accountId = route.query.accountId;
    const chatbotId = response.data.chatbot_id;

    setTimeout(() => {
      if (returnTo === 'whatsapp_management' && openPopupWhatsapp === 'true' && accountId) {
        window.location.href = `/dashboard/whatsapp_management?openpopupwhatsapp=true&accountId=${accountId}&chatbotId=${chatbotId}`;
      } else {
        window.location.href = '/dashboard/chatbots';
      }
    }, 1500);

  } catch (err) {
    // Token expiré / invalide -> session expirée : on nettoie et on renvoie au login
    // (cette page utilise axios brut, sans l'intercepteur 401 d'axiosPeelo).
    if (err.response?.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      showNotificationMessage(t('createAgent.msgSessionExpired'), 'error');
      setTimeout(() => { window.location.href = '/login'; }, 1300);
      return;
    }
    let errorMessage = t('createAgent.msgError');
    if (err.response) {
      errorMessage = err.response.data?.error || err.response.data?.message || errorMessage;
    } else if (err.request) {
      errorMessage = t('createAgent.msgNoServer');
    }
    showNotificationMessage(errorMessage, 'error');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
/* ===== Création d'agent — couleurs Peelo (#143028 vert / #DAFFD1 menthe / #FCFAF7 crème) ===== */
.create-chatbot {
  min-height: 100%;
  /* Transparent : hérite du fond du layout (.main-content / var(--bg-color)) pour
     éviter tout décalage de couleur visible avec le reste de la page. */
  background: transparent;
  padding: 32px 20px 60px;
}

.cc-wrap { max-width: 640px; margin: 0 auto; }

.cc-back {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; cursor: pointer;
  color: #143028; opacity: 0.65;
  font-size: 0.9rem; font-weight: 600;
  padding: 6px 0; margin-bottom: 20px;
  transition: opacity 0.2s;
}
.cc-back:hover { opacity: 1; }
:global(.dark) .cc-back { color: #DAFFD1; }

/* Carte principale */
.cc-card {
  background: #fff;
  border: 1px solid rgba(20, 48, 40, 0.06);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 40px 90px -40px rgba(20, 48, 40, 0.35);
}
:global(.dark) .cc-card { background: #122821; border-color: rgba(255,255,255,0.06); }

/* Bandeau d'en-tête vert Peelo */
.cc-hero {
  position: relative;
  background: linear-gradient(135deg, #143028 0%, #1b4538 100%);
  color: #fff; text-align: center;
  padding: 34px 28px 64px;
  overflow: hidden;
}
.cc-hero::after {
  content: ''; position: absolute; right: -40px; top: -40px;
  width: 160px; height: 160px; border-radius: 9999px;
  background: radial-gradient(circle, rgba(218,255,209,0.18), transparent 70%);
}
.cc-eyebrow {
  display: inline-block;
  background: rgba(218,255,209,0.18); color: #DAFFD1;
  font-size: 11px; font-weight: 900; letter-spacing: 0.16em; text-transform: uppercase;
  padding: 6px 14px; border-radius: 9999px; margin-bottom: 12px;
}
.cc-hero h1 { font-size: 1.7rem; font-weight: 800; letter-spacing: -0.03em; color: #fff; margin: 0 0 6px; position: relative; }
.cc-hero p { font-size: 0.92rem; line-height: 1.5; color: rgba(255,255,255,0.7); margin: 0; position: relative; }

/* Avatar (aperçu live) chevauchant le bas du bandeau */
.cc-avatar-zone {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  margin-top: -52px;
}
.cc-avatar {
  position: relative; width: 104px; height: 104px;
  border-radius: 9999px; cursor: pointer; display: block;
  box-shadow: 0 0 0 5px #fff, 0 18px 30px -12px rgba(20,48,40,0.45);
  transition: transform 0.2s;
}
.cc-avatar:hover { transform: scale(1.03); }
.cc-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 9999px; background: #FCFAF7; }
.cc-avatar-edit {
  position: absolute; bottom: 2px; right: 2px;
  width: 30px; height: 30px; border-radius: 9999px;
  background: #143028; color: #DAFFD1;
  display: flex; align-items: center; justify-content: center;
  border: 3px solid #fff;
}
.cc-avatar-helper { font-size: 0.74rem; font-weight: 500; color: rgba(20,48,40,0.5); }
:global(.dark) .cc-avatar-helper { color: rgba(255,255,255,0.5); }

/* Corps du formulaire */
.cc-body { padding: 18px 30px 30px; }

.cc-name-field { width: 100%; }
.cc-name-field label,
.cc-field label {
  display: block; font-size: 0.78rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: rgba(20,48,40,0.55); margin-bottom: 7px;
}
:global(.dark) .cc-name-field label, :global(.dark) .cc-field label { color: rgba(255,255,255,0.55); }
.cc-name-field input {
  width: 100%; box-sizing: border-box;
  font-size: 1.15rem; font-weight: 600;
  padding: 12px 14px;
  border: 1px solid rgba(20,48,40,0.15); border-radius: 12px;
  background: #fff; color: #143028; outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.cc-name-field input:focus { border-color: #143028; box-shadow: 0 0 0 3px rgba(20,48,40,0.08); }
.cc-hint { display: block; font-size: 0.74rem; color: rgba(20,48,40,0.45); margin-top: 6px; }
:global(.dark) .cc-hint { color: rgba(255,255,255,0.45); }

/* Toggle "plus d'options" */
.cc-more {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; margin: 22px 0 0;
  background: none; border: none; cursor: pointer;
  color: #143028; font-size: 0.85rem; font-weight: 700;
  padding: 10px;
}
:global(.dark) .cc-more { color: #DAFFD1; }
.cc-more svg { transition: transform 0.25s; }
.cc-more.open svg { transform: rotate(180deg); }

/* Section avancée */
.cc-advanced { display: flex; flex-direction: column; gap: 18px; margin-top: 8px; }
.cc-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.cc-field input,
.cc-field textarea,
.cc-field select {
  width: 100%; box-sizing: border-box;
  font-size: 0.95rem;
  padding: 11px 13px;
  border: 1px solid rgba(20,48,40,0.15); border-radius: 11px;
  background: #fff; color: #143028; outline: none;
  transition: border-color 0.2s;
}
.cc-field textarea { min-height: 84px; resize: vertical; font-family: inherit; }
.cc-field input:focus, .cc-field textarea:focus, .cc-field select:focus { border-color: #143028; }
.cc-select { position: relative; }
.cc-select select { appearance: none; -webkit-appearance: none; padding-right: 36px; cursor: pointer; }
.cc-select-arrow { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; color: rgba(20,48,40,0.5); }

/* Canaux */
.cc-channels { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.cc-channel {
  position: relative;
  display: flex; flex-direction: column; align-items: center; justify-content: flex-start; gap: 4px;
  padding: 16px 10px; border-radius: 16px; cursor: pointer; text-align: center;
  background: #fff; border: 1.5px solid rgba(20,48,40,0.12);
  color: #143028; transition: all 0.2s;
}
.cc-channel:not(.disabled):hover { border-color: rgba(20,48,40,0.35); transform: translateY(-2px); }
.cc-channel.active { border-color: #143028; background: #DAFFD1; box-shadow: 0 8px 22px -10px rgba(20,48,40,0.35); }
.cc-channel.disabled { opacity: 0.55; cursor: not-allowed; }
.cc-channel-icon {
  width: 44px; height: 44px; margin-bottom: 4px;
  border-radius: 12px; background: rgba(20,48,40,0.05);
  display: flex; align-items: center; justify-content: center; color: #143028;
}
.cc-channel.active .cc-channel-icon { background: rgba(20,48,40,0.1); }
.cc-channel-name { font-size: 0.88rem; font-weight: 800; }
.cc-channel-desc { font-size: 0.68rem; font-weight: 600; color: rgba(20,48,40,0.5); line-height: 1.2; }
.cc-channel-check { position: absolute; top: 8px; right: 8px; color: #143028; }

/* Sous-section WhatsApp */
.cc-subsection { border: 1px solid rgba(20,48,40,0.1); border-radius: 14px; overflow: hidden; }
.cc-sub-toggle {
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  background: rgba(20,48,40,0.03); border: none; cursor: pointer;
  padding: 13px 16px; color: #143028; font-size: 0.85rem; font-weight: 700;
}
.cc-sub-toggle svg { transition: transform 0.25s; }
.cc-sub-toggle.open svg { transform: rotate(180deg); }
.cc-sub-body { padding: 16px; display: flex; flex-direction: column; gap: 14px; }
.cc-pass { position: relative; }
.cc-pass input { padding-right: 42px; }
.cc-eye {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: rgba(20,48,40,0.5); padding: 4px;
}

/* Actions */
.cc-actions { display: flex; gap: 12px; margin-top: 28px; }
.cc-cancel {
  flex: 0 0 auto; padding: 13px 22px;
  background: rgba(20,48,40,0.06); color: #143028; border: none; border-radius: 9999px;
  font-size: 0.9rem; font-weight: 700; cursor: pointer; transition: background 0.2s;
}
.cc-cancel:hover { background: rgba(20,48,40,0.12); }
.cc-create {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 13px 22px;
  background: #143028; color: #fff; border: none; border-radius: 9999px;
  font-size: 0.95rem; font-weight: 800; cursor: pointer; transition: transform 0.2s, opacity 0.2s;
}
.cc-create:hover:not(:disabled) { transform: scale(1.02); }
.cc-create:disabled { opacity: 0.45; cursor: not-allowed; }

/* Loading */
.cc-loading { text-align: center; padding: 80px 20px; color: rgba(20,48,40,0.6); }
.cc-spinner {
  width: 42px; height: 42px; margin: 0 auto 16px;
  border: 3px solid rgba(20,48,40,0.12); border-top-color: #143028;
  border-radius: 9999px; animation: ccspin 0.8s linear infinite;
}
@keyframes ccspin { to { transform: rotate(360deg); } }

/* Transition repli */
.cc-collapse-enter-active, .cc-collapse-leave-active { transition: opacity 0.25s, transform 0.25s; }
.cc-collapse-enter-from, .cc-collapse-leave-to { opacity: 0; transform: translateY(-8px); }

@media (max-width: 560px) {
  .cc-hero { padding: 28px 20px 60px; }
  .cc-body { padding: 16px 20px 26px; }
  .cc-field-row { grid-template-columns: 1fr; }
  .cc-channels { grid-template-columns: 1fr; }
}
</style>
